/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useAsyncError, useLocation, useNavigation } from 'react-router';
import type { Route } from './+types/_app.search';
import type { ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchSearchProducts } from '@/lib/api/search.server';
// RGBARRY-CUSTOMIZATION (perf): compact image-heavy search hits before React Router serializes them.
import { projectProductSearchResultForTiles } from '@/lib/product/search-result-tile-projection';
import { buildProductUrlMap } from '@/lib/product/seo-url';
import { isMobileRequest } from '@/lib/routes/request-mode.server';
import type { ProductUrlMap } from '@/lib/product/product-utils';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { getLogger } from '@/lib/logger.server';
import CategoryPagination from '@/components/category-pagination';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
// RGBARRY-CUSTOMIZATION (perf): load the hidden refinement controls on first interaction.
import { LazyCategoryRefinementsPanel } from '@/components/category-refinements/lazy-panel';
import CategorySorting from '@/components/category-sorting';
import DeferredProductGrid from '@/components/product-grid';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region } from '@/components/region';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import {
    getInitialFiltersOpen,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { fetchProductRecommendations } from '@/lib/product/recommendations.server';
import { EINSTEIN_RECOMMENDERS } from '@/lib/product/einstein-recommenders';
import type { Recommendation } from '@/hooks/recommenders/use-recommenders';
import DeferredProductRecommendations from '@/components/product-recommendations/deferred';
import { useAuth } from '@/providers/auth';

@PageType({
    name: 'Search Results Page',
    description: 'Search results page with product listings and personalized content',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'searchTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of search results',
        maxComponents: 5,
    },
    {
        id: 'searchTopContent',
        name: 'Top Content Region',
        description: 'Content width region below sort/filter, above product grid',
        maxComponents: 5,
    },
    {
        id: 'searchBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class SearchPageMetadata {}

export type SearchPageData = {
    searchTerm: string;
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    // RGBARRY-CUSTOMIZATION (W-23875749): resolver's `productId -> canonical slug path` for the first page's
    // hits, so PLP tile links match production. Streamed (never blocks TTFB); degrades to slugify on failure.
    // Optional so lightweight loader-data mocks (tests, stories) can omit it — tiles then use the slugify path.
    productUrls?: Promise<ProductUrlMap>;
    // RGBARRY-CUSTOMIZATION: recently-viewed carousel shown when search returns 0 results
    emptySearchRecsPromise?: Promise<Recommendation>;
    page: ReturnType<typeof fetchPageWithComponentData>;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
};

// RGBARRY-CUSTOMIZATION (perf): keep the optional-data fallback stable without pinning stale loader data
// across same-route search navigations.
const EMPTY_RECOMMENDATIONS_PROMISE: Promise<Recommendation> = Promise.resolve({});

/**
 * Server-side loader function that fetches search results data.
 * This function runs on the server during SSR and prepares data for the search page.
 * @returns Object containing search results, refinements, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<SearchPageData> {
    const { context, request } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const offset = parseInt(searchParams.get('offset') || '0', 10);
    const q = searchParams.get('q') ?? '';
    const sort = searchParams.get('sort') ?? '';
    const refine = searchParams.getAll('refine');
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);
    const config = getConfig(context);
    const logger = getLogger(context);

    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Search: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;

    const limit = config.search.products.hits.limit;

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    // RGBARRY-CUSTOMIZATION: responsive critical hits — fewer inline tiles on mobile (phones) to cut
    // above-the-fold hydration (TBT) and LCP; desktop keeps the fuller count.
    const criticalCount = isMobileRequest(request)
        ? (config.search.products.hits.criticalMobile ?? 2)
        : (config.search.products.hits.critical ?? 4);
    const safeCriticalCount = Math.min(criticalCount, limit);
    const tileProjectionOptions = {
        tileViewType: config.search.products.images?.tile ?? 'hi-res',
        swatchViewType: config.search.products.images?.swatch ?? 'swatch',
    };

    logger.debug('Search: loader starting', { q, offset, sort, refineCount: refine.length });
    // RGBARRY-CUSTOMIZATION: guard the awaited critical search so a SCAPI ProductSearch fault degrades
    // to an empty result instead of taking down the whole search page via the route error boundary.
    let searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    try {
        searchResultCritical = projectProductSearchResultForTiles(
            await fetchSearchProducts(context, {
                q,
                limit: safeCriticalCount,
                offset,
                sort,
                refine,
                currency,
            }),
            tileProjectionOptions
        );
    } catch (e) {
        logger.error('Search PLP critical product search failed; rendering empty result', {
            q,
            error: e instanceof Error ? e.message : String(e),
        });
        searchResultCritical = {
            limit: safeCriticalCount,
            hits: [],
            query: q,
            refinements: [],
            sortingOptions: [],
            searchPhraseSuggestions: {},
            offset,
            total: 0,
        } as unknown as ShopperSearch.schemas['ProductSearchResult'];
    }
    logger.info('Search: results loaded', { query: q, total: searchResultCritical.total, offset });

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);
    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;

    // RGBARRY-CUSTOMIZATION (perf): project in the loader promise so Suspense receives one stable,
    // already-compacted reference and the streamed payload excludes unused color/image groups.
    const searchResultNonCritical = fetchSearchProducts(context, {
        q,
        limit: limit - effectiveCriticalCount,
        offset: offset + effectiveCriticalCount,
        sort,
        refine,
        currency,
    }).then((result) => projectProductSearchResultForTiles(result, tileProjectionOptions));

    // RGBARRY-CUSTOMIZATION (W-23875749): project the first page's canonical paths (critical + non-critical)
    // out of the `c_seoUrl` the int_urlresolver_scapi product_search hook stamps on each hit — no extra
    // resolver call, and the enriched search response is edge-cacheable. Chained off the already-deferred
    // non-critical fetch so it never blocks TTFB; `buildProductUrlMap` is a pure projection and the `.catch`
    // guards the fetch. Hits whose hook produced no path are omitted (slugify + /:slug/:id.html 301 fallback).
    const productUrls: Promise<ProductUrlMap> = searchResultNonCritical
        .then((nonCritical) => buildProductUrlMap([...(searchResultCritical.hits ?? []), ...(nonCritical.hits ?? [])]))
        .catch(() => ({}) as ProductUrlMap);

    return {
        searchTerm: q,
        searchResultCritical,
        searchResultNonCritical,
        productUrls,
        // RGBARRY-CUSTOMIZATION (perf): only zero-result searches can render this carousel. Avoid an
        // unused Einstein request on normal search traffic while keeping it deferred for the empty state.
        emptySearchRecsPromise:
            searchResultCritical.total === 0
                ? fetchProductRecommendations(
                      { context, request },
                      {
                          name: EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_TOP_SELLERS,
                          ...(currency ? { currency } : {}),
                      }
                  )
                : undefined,
        page: fetchPageWithComponentData(args, {
            pageId: 'search',
        }),
        pageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';

// RGBARRY-CUSTOMIZATION: classify this route for the GTM data layer's page-view event
// (see src/analytics/route-analytics.ts).
export const handle = {
    analytics: { routeType: 'search' },
};

function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

// RGBARRY-CUSTOMIZATION (perf): pin the zero-result recommendation Promise for this semantic search
// identity. The parent key remounts this child when the query, storefront, or shopper audience changes,
// while refinement-only revalidations keep the resolved carousel mounted instead of feeding <Await> a fresh Promise.
function EmptySearchRecommendations({ data, title }: { data?: Promise<Recommendation>; title: string }) {
    const [pinnedRecommendations] = useState(() => data ?? EMPTY_RECOMMENDATIONS_PROMISE);

    return (
        <div className="mt-12">
            <DeferredProductRecommendations
                recommenderName={EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_TOP_SELLERS}
                recommenderTitle={title}
                data={pinnedRecommendations}
                className="max-w-none px-0"
            />
        </div>
    );
}

export default function SearchPage({
    loaderData: {
        searchTerm,
        searchResultCritical,
        searchResultNonCritical,
        productUrls,
        emptySearchRecsPromise,
        page,
        pageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
    },
}: {
    loaderData: SearchPageData;
}) {
    const { t } = useTranslation('search');
    const { t: tProduct } = useTranslation('product');
    const config = useConfig();
    const { site } = useSite();
    const auth = useAuth();
    const limit = config.search.products.hits.limit;

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);

    const criticalCount = searchResultCritical.hits?.length ?? 0;
    // RGBARRY-CUSTOMIZATION: report the complete deferred window so DeferredProductGrid can reserve
    // every tile's space. It retains PR #219's cap internally by using lightweight slots after tile eight.
    const nonCriticalCount = Math.max(
        0,
        Math.min(limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedSearchRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const emptySearchRecommendationKey = JSON.stringify([
        site.id,
        locale,
        currency,
        searchTerm,
        auth?.userType ?? null,
        auth?.usid ?? null,
        auth?.customerId ?? null,
    ]);
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInSearch({
                    searchInputText: searchTerm,
                    product,
                });
            }
        },
        [analytics, searchTerm]
    );

    useEffect(() => {
        // Only track if we haven't already tracked this search
        if (analyticsKey !== lastTrackedSearchRef.current) {
            lastTrackedSearchRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        void analytics.trackViewSearch({
                            searchInputText: searchTerm,
                            searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                            sort:
                                searchResultCritical.selectedSortingOption ||
                                searchResultCritical.sortingOptions?.[0]?.label ||
                                '',
                            refinements: searchResultCritical.selectedRefinements ?? {},
                            offset: searchResultCritical.offset,
                            limit,
                            total: searchResultCritical.total,
                        });
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, searchTerm, analyticsKey, nonCriticalPromise]);

    return (
        <>
            <SeoMeta
                title={
                    searchTerm
                        ? t('titleWithQuery', { query: searchTerm, defaultValue: `Results for "${searchTerm}"` })
                        : t('title', { defaultValue: 'Search' })
                }
                description={
                    searchTerm
                        ? t('meta.descriptionWithQuery', {
                              query: searchTerm,
                              count: searchResultCritical.total,
                              defaultValue: `${searchResultCritical.total} results for "${searchTerm}"`,
                          })
                        : t('meta.description', { defaultValue: 'Search our store for products' })
                }
                openGraph={{ type: 'website', url: pageUrl }}
            />
            <div className="pb-16">
                <div className="section-container">
                    <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <p>{t('results')}</p>
                            <h1 className="text-3xl font-bold leading-none tracking-[-0.75px] text-card-foreground">
                                {searchTerm} ({searchResultCritical.total})
                            </h1>
                        </div>
                        {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                            <div className="flex-shrink-0">
                                <CategorySorting result={searchResultCritical} />
                            </div>
                        )}
                    </div>

                    {/* searchTopFullWidth */}
                    <Region className="mb-8" page={page} regionId="searchTopFullWidth" />

                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Filters toggle button - mobile only (above panel) */}
                        <div className="lg:hidden">
                            <FiltersButton
                                onClick={toggleFiltersOpen}
                                isActive={filtersOpen}
                                selectedFiltersCount={selectedFiltersCount}
                            />
                        </div>

                        {/* RGBARRY-CUSTOMIZATION (perf): preserve the existing panel layout while
                            deferring its full refinement graph until the shopper opens it. */}
                        <LazyCategoryRefinementsPanel
                            open={filtersOpen}
                            className="w-full lg:w-64 lg:flex-shrink-0"
                            result={searchResultCritical}
                            refine={refine}
                        />

                        <div className="flex-grow">
                            {/* Filters toggle button - desktop only (inside content area) */}
                            <div className="mb-4 hidden lg:block">
                                <FiltersButton
                                    onClick={toggleFiltersOpen}
                                    isActive={filtersOpen}
                                    selectedFiltersCount={selectedFiltersCount}
                                />
                            </div>

                            <ActiveFilters result={searchResultCritical} />

                            {/* searchTopContent */}
                            <Region className="mb-8" page={page} regionId="searchTopContent" />

                            <DeferredProductGrid
                                key={productGridDataKey}
                                critical={searchResultCritical.hits ?? []}
                                nonCritical={nonCriticalPromise}
                                nonCriticalCount={nonCriticalCount}
                                hasRefinementsPanel={filtersOpen}
                                isLoading={isProductGridLoading}
                                handleProductClick={handleProductClick}
                                resolvedProductPaths={productUrls}
                                errorElement={<ProductGridError />}
                            />

                            {/* RGBARRY-CUSTOMIZATION: recently-viewed carousel shown when search returns 0 results.
                                SFRA's search-no-results slot uses `viewed-recently`, but that rule-id 404s in the
                                Einstein realm — SFNext uses `recently-viewed` (the one that exists). */}
                            {searchResultCritical.total === 0 && (
                                <EmptySearchRecommendations
                                    key={emptySearchRecommendationKey}
                                    data={emptySearchRecsPromise}
                                    title={tProduct('recommendations.recentlyViewed')}
                                />
                            )}

                            {searchResultCritical.total > 1 && (
                                <div className="mt-10">
                                    <CategoryPagination
                                        limit={limit}
                                        offset={searchResultCritical.offset}
                                        total={searchResultCritical.total}
                                    />
                                </div>
                            )}

                            {/* searchBottom */}
                            <Region className="mt-8" page={page} regionId="searchBottom" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
